/**
 * Main Javascript entry point
 *
 * all imported modules will be bundled to main.js
 */
import './polyfills/process';
import React from 'react';
import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import App from './App';
import { createRoot } from 'react-dom/client';
import en from '../locales/en.json';
import de from '../locales/de.json';
import fr from '../locales/fr.json';
import { registerSW } from 'virtual:pwa-register';

// Register the service worker
// Handles updates for unattended digital signage environments
const updateSW = registerSW({
  immediate: true, // Forces immediate check on load
  onNeedRefresh() {
    console.log('[RCM] New version found. Triggering reload...');
    // Forces the service worker to skip waiting and activate immediately,
    // which automatically triggers a page refresh to apply changes.
    updateSW(true); 
  },
  onOfflineReady() {
    console.log('[RCM] App is fully precached and ready for offline operation.');
  },
});

// Periodic background check loop for signage kiosks
setInterval(async () => {
  console.log('[RCM] Checking server for application updates...');
  // Asks the browser to check the server for a newer sw.js asset
  try {
    // 1. Fetch the active service worker registration object
    const registration = await navigator.serviceWorker.getRegistration();
    
    if (registration) {
      // 2. Ask the browser to download and check the server's sw.js file
      // If a change is detected, it will trigger onNeedRefresh() hook above.
      await registration.update();
      console.log('[RCM] Service worker update check complete.');
    }
  } catch (error) {
    console.error('[RCM] Failed to check for service worker updates:', error);
  }}, 60 * 60 * 1000);

i18n.use(LanguageDetector)
    .use(initReactI18next)
    .init({
        fallbackLng: 'en',
        detection: {
            order: ['navigator'],
            convertDetectedLanguage: (lng) => lng.split('-')[0]
        },
        resources: {
            en: { translation: en },
            de: { translation: de },
            fr: { translation: fr }
        },
        react: { useSuspense: false }
    });

const domNode = document.getElementById('main-app') as Element | DocumentFragment;
const root = createRoot(domNode);
root.render(<App />);